import { createStore } from 'zustand'
import type { Mutate, StoreApi } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import { createAppSlice } from './appSlice'
import type { AppActions, AppState } from './appSlice'
import { createCapabilitySlice } from './capabilitySlice'
import type { CapabilityActions, CapabilityState } from './capabilitySlice'
import { createChannelSlice } from './channelSlice'
import type { ChannelActions, ChannelState } from './channelSlice'
import { createDirtySlice } from './dirtySlice'
import type { DirtyActions, DirtyState } from './dirtySlice'
import { createEditorSlice } from './editorSlice'
import type { EditorActions, EditorState } from './editorSlice'
import { createModeSlice } from './modeSlice/modeSlice'
import type { ModeActions, ModeState } from './modeSlice/modeSlice'
import { createPostSlice } from './postSlice'
import type { PostState, PostActions } from './postSlice'
import { createSaveErrorsSlice } from './saveErrorsSlice'
import type { SaveErrorsState, SaveErrorsActions } from './saveErrorsSlice'

export type ComposerState = AppState &
  CapabilityState &
  ChannelState &
  DirtyState &
  EditorState &
  ModeState &
  PostState &
  SaveErrorsState
export type ComposerActions = AppActions &
  CapabilityActions &
  ChannelActions &
  DirtyActions &
  EditorActions &
  ModeActions &
  PostActions &
  SaveErrorsActions

export type ComposerStore = Mutate<
  StoreApi<ComposerState & ComposerActions>,
  [['zustand/devtools', never], ['zustand/immer', never]]
>

export type InitializeComposerProps = Pick<
  ComposerState,
  | 'mode'
  | 'draftMode'
  | 'previewMode'
  | 'ideaId'
  | 'templateId'
  | 'prefillAiPrompt'
  | 'initialPost'
  | 'prefillPostData'
  | 'duplicatedFrom'
  | 'selectedTagIds'
  | 'selectedChannels'
  | 'profiles'
  | 'channels'
  | 'activeSidePanel'
> &
  Partial<Pick<ComposerState, 'activeChannel'>> & {
    /**
     * ContentItem id when editing a channel-less draft. Not stored as state:
     * it seeds `omni.draftId` once at creation so Save routes to
     * updateContentItemDraft. The Flux→Zustand mirror never writes `draftId`,
     * so the seed survives content syncing.
     */
    contentItemId?: string
    /**
     * The ContentItem's `title`, alongside `contentItemId`. Not stored as
     * state: it seeds `omni.title` once at creation, same as `contentItemId`
     * seeds `omni.draftId` — there is no Flux equivalent to mirror.
     */
    contentItemTitle?: string | null
  }

const defaultStoreProps: InitializeComposerProps = {
  mode: 'create',
  draftMode: false,
  previewMode: false,
  ideaId: undefined,
  activeChannel: 'omni',
  selectedTagIds: [],
  selectedChannels: [],
  profiles: {},
  channels: {},
  activeSidePanel: 'postPreview',
}
// Defensively clone mutable props to avoid shared references
const buildInitialProps = (
  props: Partial<InitializeComposerProps>,
): InitializeComposerProps => {
  const merged = {
    ...defaultStoreProps,
    ...props,
  }

  return {
    ...merged,
    selectedTagIds: [...merged.selectedTagIds],
    selectedChannels: [...merged.selectedChannels],
    profiles: { ...merged.profiles },
  }
}

export const createComposerStore = (
  props: Partial<InitializeComposerProps>,
): ComposerStore => {
  // contentItemId/contentItemTitle are not composer state — they seed
  // omni.draftId/omni.title below and must not leak into the spread that
  // initializes the slices.
  const { contentItemId, contentItemTitle, ...storeProps } = props
  const initialProps = buildInitialProps(storeProps)

  const composerStore = createStore<ComposerState & ComposerActions>()(
    devtools(
      immer((...a) => ({
        ...createAppSlice(...a),
        ...createCapabilitySlice(...a),
        ...createChannelSlice(...a),
        ...createDirtySlice(...a),
        ...createEditorSlice(...a),
        ...createModeSlice(...a),
        ...createPostSlice(...a),
        ...createSaveErrorsSlice(...a),
        ...initialProps,
      })),
      {
        name: 'composer',
      },
    ),
  )

  if (contentItemId) {
    composerStore.getState().setOmniDraftId(contentItemId)
  }
  if (contentItemTitle) {
    composerStore.getState().setOmniTitle(contentItemTitle)
  }

  return composerStore
}

/**
 * Set 'store' as a singleton to allow access outside of React components.
 * This functionality should ideally be avoided, but is necessary for
 * certain legacy code that requires direct access to the store.
 */
let store: ComposerStore | null = null

/**
 * Initializes the composer store for a new session from the given props.
 *
 * Always creates a fresh store: a new ComposerProvider mount is always a new
 * composer session (its own `storeRef` guard already prevents redundant
 * calls for the same mount), so this session's props — e.g. a channel-less
 * draft's empty `selectedChannels` — must fully replace whatever store was
 * live before it.
 */
export const initComposerStore = (
  props: Partial<InitializeComposerProps> = {},
): ComposerStore => {
  store = createComposerStore(props)
  return store
}

/**
 * Retrieves the composer store instance.
 * If the store has not been initialized, it throws an error.
 * This is to prevent accidental access before initialization.
 * @returns The composer store instance.
 */
export const getComposerStore = (): ComposerStore => {
  if (!store) {
    throw new Error('Composer store has not been initialized yet.')
  }
  return store
}

/**
 * Like `getComposerStore`, but returns `null` instead of throwing when no
 * composer session is live. For lookups that must stay safe outside a
 * composer session — e.g. legacy Flux dispatch logic exercised by narrow unit
 * tests that never mount `ComposerProvider`.
 */
export const peekComposerStore = (): ComposerStore | null => store

/**
 * Clears the composer store instance.
 * This is called on composer close to prevent state retention.
 */
export const clearComposerStore = (): void => {
  store = null
}

/**
 * Clears the composer store only if `instance` is still the live one.
 *
 * `ComposerDialog`'s close handler defers the clear by one tick (see its
 * `setTimeout` call) so React finishes unmounting first. If a new session
 * starts within that tick (fast close-then-reopen, or "Create Another"),
 * the deferred clear must not null out the *new* store — it should capture
 * the store it means to clear before deferring, then use this instead of
 * the unconditional `clearComposerStore`.
 */
export const clearComposerStoreIfCurrent = (instance: ComposerStore): void => {
  if (store === instance) {
    store = null
  }
}

/**
 * Captures the live store (if any) and clears it on the next tick via
 * `clearComposerStoreIfCurrent`, so a session that starts within that tick
 * isn't clobbered. No-ops when there's no live store, so a close handler
 * that runs more than once (e.g. a stray dismiss after the store's already
 * gone) stays safe to call repeatedly.
 */
export const deferComposerStoreClear = (): void => {
  const closingStore = peekComposerStore()
  if (!closingStore) return
  setTimeout(() => {
    clearComposerStoreIfCurrent(closingStore)
  }, 0)
}
